import { View, styled } from 'tamagui'
import { useAppStore } from '@/store'
import { useGlobalShortcuts } from '@/hooks'
import { ServerRail } from './ServerRail'
import { ChannelSidebar } from './ChannelSidebar'
import { MainContent } from './MainContent'
import { ThreadPanel } from './ThreadPanel'
import { VoiceBar } from './VoiceBar'
import { CommandPalette } from '../app/palette/CommandPalette'
import { UserProfileModal } from '../app/profile/UserProfileModal'
import { HomeDashboard } from '../app/home/HomeDashboard'
import { ServerSettings } from '../app/settings/ServerSettings'
import { UserSettings } from '../app/settings/UserSettings'
import { DMSidebar } from './DMSidebar'
import { DMView } from '../app/dm/DMView'
import { FriendsList } from '../app/dm/FriendsList'
import { CreateServerModal } from '../app/server/CreateServerModal'
import { JoinServerModal } from '../app/server/JoinServerModal'
import { AddFriendModal } from '../app/dm/AddFriendModal'
import { ServerContextMenu } from '../app/server/ServerContextMenu'
import { ChannelContextMenu } from '../app/channel/ChannelContextMenu'
import { SearchModal } from '../app/search/SearchModal'
import { PinnedMessages } from '../app/channel/PinnedMessages'
import { InboxView } from '../app/inbox/InboxView'
import { EmojiPicker } from '../app/emoji/EmojiPicker'
import { MessageDeleteConfirm } from '../app/message/MessageDeleteConfirm'
import { ImageLightbox } from '../app/message/ImageLightbox'
import { MessageContextMenu } from '../app/message/MessageContextMenu'
import { ScheduledEventsPanel } from '../app/server/ScheduledEventsPanel'
import { ServerWelcomeScreen } from '../app/server/ServerWelcomeScreen'
import { ThreadListPanel } from '../app/message/ThreadListPanel'
import { KeyboardShortcutsModal } from '../app/settings/KeyboardShortcutsModal'
import { ToastContainer } from '../common/ToastContainer'
import { ConnectionBanner } from '../common/ConnectionBanner'

const Shell = styled(View, {
  width: '100vw',
  height: '100vh',
  backgroundColor: '$surface',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
})

const DragRegion = styled(View, {
  height: 32,
  width: '100%',
  backgroundColor: '$surface',
  flexDirection: 'row',
  alignItems: 'center',
  paddingLeft: 80,
  // @ts-expect-error -- Tauri-specific
  style: { WebkitAppRegion: 'drag' },
})

const ContentArea = styled(View, {
  flex: 1,
  flexDirection: 'row',
  overflow: 'hidden',
})

function ViewContent() {
  const { currentView, threadPanelOpen, threadListOpen, activeDmChannelId, pinnedOpen, activeChannelId } = useAppStore()

  switch (currentView) {
    case 'home':
      return <HomeDashboard />

    case 'server-settings':
      return <ServerSettings />

    case 'user-settings':
      return <UserSettings />

    case 'dm':
      return (
        <>
          <DMSidebar />
          {activeDmChannelId ? <DMView /> : <FriendsList />}
        </>
      )

    case 'server':
    default:
      return (
        <>
          <ChannelSidebar />
          <MainContent />
          {pinnedOpen && <PinnedMessages />}
          {threadListOpen && activeChannelId && <ThreadListPanel channelId={activeChannelId} />}
          {threadPanelOpen && <ThreadPanel />}
        </>
      )
  }
}

export function AppShell() {
  const {
    commandPaletteOpen,
    voiceChannelId,
    profileModalUserId,
    createServerModalOpen,
    joinServerModalOpen,
    addFriendModalOpen,
    contextMenu,
    closeContextMenu,
    searchOpen,
    inboxOpen,
    emojiPickerOpen,
    deleteMessageId,
    imagePreviewUrl,
    scheduledEventsOpen,
    welcomeScreenServerId,
    keyboardShortcutsOpen,
  } = useAppStore()

  useGlobalShortcuts()

  return (
    <Shell>
      <DragRegion />
      <ConnectionBanner />
      <ContentArea>
        <ServerRail />
        <ViewContent />
      </ContentArea>
      {voiceChannelId && <VoiceBar />}
      {commandPaletteOpen && <CommandPalette />}
      {profileModalUserId && <UserProfileModal />}
      {createServerModalOpen && <CreateServerModal />}
      {joinServerModalOpen && <JoinServerModal />}
      {addFriendModalOpen && <AddFriendModal />}
      {searchOpen && <SearchModal />}
      {inboxOpen && <InboxView />}
      {emojiPickerOpen && <EmojiPicker onSelect={(emoji) => { /* insert emoji at cursor */ }} />}
      {deleteMessageId && <MessageDeleteConfirm />}
      {imagePreviewUrl && <ImageLightbox />}
      {contextMenu.type === 'message' && contextMenu.targetId && <MessageContextMenu />}
      {scheduledEventsOpen && <ScheduledEventsPanel />}
      {welcomeScreenServerId && <ServerWelcomeScreen />}
      {keyboardShortcutsOpen && <KeyboardShortcutsModal />}
      {contextMenu.type === 'server' && contextMenu.targetId && (
        <ServerContextMenu
          serverId={contextMenu.targetId}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
        />
      )}
      {contextMenu.type === 'channel' && contextMenu.targetId && (
        <ChannelContextMenu
          channelId={contextMenu.targetId}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
        />
      )}
      <ToastContainer />
    </Shell>
  )
}
